import React, { useState, useEffect, useCallback } from "react";
import "./Schedule.scss";
import axios from "../../api/axios";
import { getCampaignsByDateRoute, updateStreamRoute, getCampaignByIdRoute, removeStreamChannelRoute } from "../../api/routes";
import { IconJsxer } from "../../helpers/IconHelper";
import config from "../../config";
import DropDownList from "../../components/forms/DropDownList/DropDownList";
import DateSelect from "../../components/forms/DateSelect/DateSelect";
import { getTimezoneOptions, parseTimezone } from "../../helpers/TimeZones";
import { getDefaultTimezone, convertFormDateTimeTimezoneToUnixTimestamp, getTzAdjustedDateObject } from "../../helpers/Dates";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { TOP_LEVEL_NAV_ROUTES } from "../../config/NavRoutes";
import { STREAM_STATUS_TYPES } from "../../helpers/Streams";
import AddStreamChannel from "../Campaigns/AddStreamChannel";

const Schedule = () => {
    const navigate = useNavigate();
    const [viewType, setViewType] = useState('week'); // 'day', 'week', 'month'
    const [currentDate, setCurrentDate] = useState(new Date());
    const [campaignsData, setCampaignsData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Time formatting and timezone state
    const [timeFormat, setTimeFormat] = useState('24h'); // '12h' or '24h'
    const [selectedTimezone, setSelectedTimezone] = useState(null);
    
    // Modal state for stream details
    const [selectedStream, setSelectedStream] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    // Edit modal state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editStreamData, setEditStreamData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Add streamer modal state
    const [showAddStreamer, setShowAddStreamer] = useState(false);
    
    // Month view expanded days state
    const [expandedDays, setExpandedDays] = useState(new Set());
    
    // Track if changes have been made to the edit form
    const [hasEditChanges, setHasEditChanges] = useState(false);
    const [originalEditData, setOriginalEditData] = useState(null);
    
    // Error modal state
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorModalData, setErrorModalData] = useState(null);

    const VIEW_OPTIONS = [
        { id: 'day', label: 'Day', index: 0 },
        { id: 'week', label: 'Week', index: 1 },
        { id: 'month', label: 'Month', index: 2 }
    ];

    const TIME_FORMAT_OPTIONS = [
        { id: '24h', label: '24 hour time', index: 0 },
        { id: '12h', label: '12 hour time', index: 1 }
    ];

    // Initialize timezone options
    const [timezoneOptions, setTimezoneOptions] = useState([]);

    useEffect(() => {
        try {
            const tzOptions = getTimezoneOptions();
            setTimezoneOptions(tzOptions);
            
            // Set default timezone to user's local timezone
            const defaultTz = getDefaultTimezone();
            const defaultTzOption = tzOptions.find(tz => tz.value === defaultTz) || 
                                   tzOptions.find(tz => tz.value === 'GMT') ||
                                   { id: 'gmt', label: 'GMT (UTC+00:00) UTC', value: 'GMT', index: 0, offset: 0 };
            setSelectedTimezone(defaultTzOption);
        } catch (error) {
            console.warn('Failed to load timezone options:', error);
            // Fallback to basic GMT option
            const fallbackTz = { id: 'gmt', label: 'GMT (UTC+00:00) UTC', value: 'GMT', index: 0, offset: 0 };
            setTimezoneOptions([fallbackTz]);
            setSelectedTimezone(fallbackTz);
        }
    }, []);

    const updateFilterHandler = (e, dropDownId, item) => {
        switch (dropDownId) {
            case 'viewType':
                const newViewType = item.id;
                const today = new Date();
                let newDate = new Date(currentDate);
                
                // Determine the appropriate date for the new view
                if (viewType === 'week' && newViewType === 'month') {
                    // Week to Month: Pick month with most crossover (4+ days)
                    const startOfWeek = new Date(currentDate);
                    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
                    const endOfWeek = new Date(startOfWeek);
                    endOfWeek.setDate(startOfWeek.getDate() + 6);
                    
                    // Count days in each month
                    const month1Days = [];
                    const month2Days = [];
                    
                    for (let i = 0; i < 7; i++) {
                        const day = new Date(startOfWeek);
                        day.setDate(startOfWeek.getDate() + i);
                        if (day.getMonth() === startOfWeek.getMonth()) {
                            month1Days.push(day);
                        } else {
                            month2Days.push(day);
                        }
                    }
                    
                    // Pick the month with 4 or more days
                    newDate = month1Days.length >= 4 ? month1Days[0] : month2Days[0];
                    
                    // If today is in this week, use today's month instead
                    if (today >= startOfWeek && today <= endOfWeek) {
                        newDate = new Date(today);
                    }
                } else if (viewType === 'month' && newViewType === 'week') {
                    // Month to Week: Pick first week of month, or week containing today if today is in this month
                    const firstOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                    
                    if (today.getMonth() === currentDate.getMonth() && today.getFullYear() === currentDate.getFullYear()) {
                        newDate = new Date(today);
                    } else {
                        newDate = firstOfMonth;
                    }
                } else if (viewType === 'month' && newViewType === 'day') {
                    // Month to Day: Pick first day of month, or today if today is in this month
                    if (today.getMonth() === currentDate.getMonth() && today.getFullYear() === currentDate.getFullYear()) {
                        newDate = new Date(today);
                    } else {
                        newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                    }
                } else if (viewType === 'week' && newViewType === 'day') {
                    // Week to Day: Pick first day of week (Sunday), or today if today is in this week
                    const startOfWeek = new Date(currentDate);
                    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
                    const endOfWeek = new Date(startOfWeek);
                    endOfWeek.setDate(startOfWeek.getDate() + 6);
                    
                    if (today >= startOfWeek && today <= endOfWeek) {
                        newDate = new Date(today);
                    } else {
                        newDate = startOfWeek;
                    }
                }
                // For day to week/month, currentDate stays the same (day is within the week/month)
                
                setCurrentDate(newDate);
                setViewType(newViewType);
                break;
            case 'timeFormat':
                setTimeFormat(item.id);
                break;
            case 'timezone':
                setSelectedTimezone(item);
                break;
            default:
                break;
        }
    };

    const fetchCampaignList = useCallback(() => {
        let startDate, endDate;
        
        if (viewType === 'day') {
            // For day view, fetch from the previous day to catch streams that extend into today
            startDate = new Date(currentDate);
            startDate.setDate(currentDate.getDate() - 1);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(currentDate);
            endDate.setHours(23, 59, 59, 999);
        } else if (viewType === 'week') {
            // Start of week (Sunday)
            startDate = new Date(currentDate);
            startDate.setDate(currentDate.getDate() - currentDate.getDay());
            startDate.setHours(0, 0, 0, 0);
            // End of week (Saturday)
            endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + 6);
            endDate.setHours(23, 59, 59, 999);
        } else { // month
            startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
            endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
            endDate.setHours(23, 59, 59, 999);
        }

        return axios
            .get(getCampaignsByDateRoute, { 
                params: { 
                    startDate: startDate.getTime(), 
                    endDate: endDate.getTime(),
                    searchTerm: searchTerm 
                }, 
                withCredentials: true 
            })
            .then((response) => {
                setCampaignsData(response.data.campaigns);
                return response.data.campaigns;
            })
            .catch((error) => {
                toast.error(error?.msg || error?.response?.data?.msg || "Failed to fetch campaigns");
                throw error;
            });
    }, [currentDate, viewType, searchTerm]);

    useEffect(() => {
        fetchCampaignList();
    }, [currentDate, viewType, fetchCampaignList]);

    // Auto-scroll to current time on load - only if viewing today
    useEffect(() => {
        // if (viewType === 'week' || viewType === 'day') {
        //     const today = new Date();
        //     const isViewingToday = viewType === 'day' 
        //         ? currentDate.toDateString() === today.toDateString()
        //         : currentDate.getTime() >= today.getTime() - (today.getDay() * 24 * 60 * 60 * 1000) &&
        //           currentDate.getTime() <= today.getTime() + ((6 - today.getDay()) * 24 * 60 * 60 * 1000);
            
        //     setTimeout(() => {
        //         if (isViewingToday) {
        //             const currentHour = new Date().getHours();
        //             const timeElement = document.querySelector(`.hour-slot:nth-child(${currentHour + 1})`);
        //             if (timeElement) {
        //                 timeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        //             }
        //         } else {
        //             const calendarContent = document.querySelector('.calendar-content');
        //             if (calendarContent) {
        //                 calendarContent.scrollTo({ top: 0, behavior: 'smooth' });
        //             }
        //         }
        //     }, 100);
        // }
    }, [viewType, currentDate]);

    const navigateDate = (direction) => {
        const newDate = new Date(currentDate);
        
        if (viewType === 'day') {
            newDate.setDate(currentDate.getDate() + (direction === 'next' ? 1 : -1));
        } else if (viewType === 'week') {
            newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7));
        } else { // month
            newDate.setMonth(currentDate.getMonth() + (direction === 'next' ? 1 : -1));
        }
        
        setCurrentDate(newDate);
    };

    const getDateRangeText = () => {
        if (viewType === 'day') {
            return currentDate.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        } else if (viewType === 'week') {
            const startOfWeek = new Date(currentDate);
            startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);
            
            return `${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        } else { // month
            return currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
        }
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    // Convert a date to the selected timezone
    const convertToSelectedTimezone = (date) => {
        if (!selectedTimezone || selectedTimezone.offset === undefined) {
            return date;
        }
        const offsetMs = selectedTimezone.offset * 60 * 60 * 1000;
        const utcTime = date.getTime() + (date.getTimezoneOffset() * 60 * 1000);
        return new Date(utcTime + offsetMs);
    };

    const formatTime = (date, timezone = null) => {
        // Date is already converted to the selected timezone in getStreamsForDay
        // So we can just format it directly
        const options = {
            hour: timeFormat === '12h' ? 'numeric' : '2-digit',
            minute: '2-digit',
            hour12: timeFormat === '12h'
        };
        
        return date.toLocaleTimeString([], options);
    };

    const handleStreamClick = (stream) => {
        setSelectedStream(stream);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedStream(null);
    };

    // Generate time options in 15-minute increments
    const generateTimeOptions = () => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 15) {
                const timeValue = hour * 3600 + minute * 60; // Convert to seconds
                const displayHour = hour.toString().padStart(2, '0');
                const displayMinute = minute.toString().padStart(2, '0');
                options.push({
                    id: `${displayHour}:${displayMinute}`,
                    label: `${displayHour}:${displayMinute}`,
                    value: timeValue,
                    index: options.length
                });
            }
        }
        // Add 24:00 as end option
        options.push({
            id: '24:00',
            label: '24:00',
            value: 24 * 3600,
            index: options.length
        });
        return options;
    };

    const timeOptions = generateTimeOptions();

    // Generate stream type options
    const streamTypeOptions = Object.keys(STREAM_STATUS_TYPES).map((key, index) => ({
        id: key,
        label: STREAM_STATUS_TYPES[key].displayName,
        value: STREAM_STATUS_TYPES[key].dbString,
        index: index
    }));

    // Open campaign edit page
    const handleOpenCampaign = (campaignId) => {
        navigate(TOP_LEVEL_NAV_ROUTES.CAMPAIGN_OVERVIEW.replace(':id', campaignId));
    };

    // Open edit modal
    const handleEditStream = () => {
        if (!selectedStream) return;
        
        console.log('Opening edit modal for stream:', selectedStream);
        console.log('Stream channels:', selectedStream.channels);
        
        // Use the calendar's selected timezone for editing (not the stream's stored timezone)
        const editTimezone = selectedTimezone || parseTimezone('GMT');
        const timezoneDiffSecs = editTimezone.offset * 3600;
        
        // Use the already-converted times from the calendar display
        // These are already in the selected timezone
        const streamDate = new Date(selectedStream.startTime);
        streamDate.setHours(0, 0, 0, 0);
        
        // Convert start/end times to time of day in seconds
        let startTimeOfDay = selectedStream.startTime.getHours() * 3600 + 
                            selectedStream.startTime.getMinutes() * 60 + 
                            selectedStream.startTime.getSeconds();
        
        let endTimeOfDay = selectedStream.endTime.getHours() * 3600 + 
                          selectedStream.endTime.getMinutes() * 60 + 
                          selectedStream.endTime.getSeconds();
        
        const newEditData = {
            ...selectedStream,
            streamDate: streamDate,
            startTimeOfDay: startTimeOfDay,
            endTimeOfDay: endTimeOfDay,
            timezone: editTimezone, // Use calendar's selected timezone
            selectedStreamType: selectedStream.status || 'scheduled'
        };
        
        console.log('Setting editStreamData to:', newEditData);
        console.log('Edit data channels:', newEditData.channels);
        
        setEditStreamData(newEditData);
        
        // Store original data for comparison
        setOriginalEditData({
            ...selectedStream,
            streamDate: streamDate,
            startTimeOfDay: startTimeOfDay,
            endTimeOfDay: endTimeOfDay,
            timezone: editTimezone, // Use calendar's selected timezone
            selectedStreamType: selectedStream.status || 'scheduled'
        });
        
        setHasEditChanges(false);
        setIsEditModalOpen(true);
        setIsModalOpen(false);
    };

    // Close edit modal
    const closeEditModal = () => {
        setEditStreamData(null);
        setOriginalEditData(null);
        setHasEditChanges(false);
        setIsEditModalOpen(false);
    };
    
    // Go back from edit to view modal
    const goBackToViewModal = () => {
        setIsEditModalOpen(false);
        setIsModalOpen(true);
        setHasEditChanges(false);
    };
    
    // Delete stream function
    const handleDeleteStream = () => {
        if (window.confirm('Are you sure you want to delete this stream?')) {
            // TODO: Implement delete stream API call
            console.log('Deleting stream:', editStreamData._id);
            closeEditModal();
        }
    };
    
    // Add streamer handlers
    const handleAddStreamer = () => {
        setShowAddStreamer(true);
    };
    
    const handleCloseAddStreamer = async () => {
        setShowAddStreamer(false);
        
        try {
            // Fetch the updated campaign data directly
            if (editStreamData && editStreamData._id && editStreamData.campaign._id) {
                console.log('Fetching updated campaign data for:', editStreamData.campaign._id);
                const response = await axios.get(getCampaignByIdRoute, { 
                    params: { campaignId: editStreamData.campaign._id },
                    withCredentials: true 
                });
                console.log('Campaign response:', response.data);
                const updatedCampaign = response.data.campaign;
                const updatedStream = updatedCampaign.streams.find(s => s._id === editStreamData._id);
                console.log('Updated stream found:', updatedStream);
                console.log('Updated stream channels:', updatedStream?.channels);
                
                if (updatedStream) {
                    // Update the edit stream data with the new channels
                    setEditStreamData(prev => {
                        console.log('Updating editStreamData channels from:', prev.channels, 'to:', updatedStream.channels);
                        return {
                            ...prev,
                            channels: updatedStream.channels || []
                        };
                    });
                    
                    // Also update the selected stream so the info popup reflects changes
                    setSelectedStream(prev => ({
                        ...prev,
                        channels: updatedStream.channels || []
                    }));
                }
            }
            
            // Also refresh the campaign list for the calendar view
            await fetchCampaignList();
        } catch (error) {
            console.error('Error refreshing stream data:', error);
        }
    };
    
    // Handle removing a streamer
    const handleRemoveStreamer = async (indexToRemove) => {
        if (!editStreamData || !editStreamData.channels[indexToRemove]) return;
        
        const streamerToRemove = editStreamData.channels[indexToRemove];
        
        try {
            // Call backend to remove the streamer
            await axios.post(removeStreamChannelRoute, {
                campaignID: editStreamData.campaign._id,
                streamID: editStreamData._id,
                twitchHandle: streamerToRemove.twitchHandle
            });
            
            // Update local state
            const newChannels = editStreamData.channels.filter((_, index) => index !== indexToRemove);
            
            setEditStreamData(prev => ({
                ...prev,
                channels: newChannels
            }));
            
            // Also update the selected stream so the info popup reflects changes
            setSelectedStream(prev => ({
                ...prev,
                channels: newChannels
            }));
            
            // Refresh the campaign list
            fetchCampaignList();
            
            toast.success(`Removed ${streamerToRemove.twitchHandle} from stream`);
        } catch (error) {
            console.error('Error removing streamer:', error);
            toast.error('Failed to remove streamer');
        }
    };

    // Handle edit form field changes
    const handleEditFieldChange = (field, value) => {
        setEditStreamData(prev => {
            const newData = {
                ...prev,
                [field]: value
            };
            
            // Check if changes have been made by comparing with original data
            const hasChanges = originalEditData && (
                newData.streamDate?.getTime() !== originalEditData.streamDate?.getTime() ||
                newData.startTimeOfDay !== originalEditData.startTimeOfDay ||
                newData.endTimeOfDay !== originalEditData.endTimeOfDay ||
                newData.timezone?.value !== originalEditData.timezone?.value ||
                newData.selectedStreamType !== originalEditData.selectedStreamType
            );
            
            setHasEditChanges(hasChanges);
            
            return newData;
        });
    };

    // Save edited stream
    const handleSaveEditedStream = async (e) => {
        e.preventDefault();
        if (isSubmitting || !editStreamData) return;
        
        setIsSubmitting(true);
        
        try {
            // Convert form data back to UTC timestamps
            const utcStartTime = convertFormDateTimeTimezoneToUnixTimestamp(
                editStreamData.streamDate,
                editStreamData.startTimeOfDay,
                editStreamData.timezone
            );
            
            // If end time is before start time, it means it crosses midnight (next day)
            let endTimeOfDay = editStreamData.endTimeOfDay;
            if (endTimeOfDay < editStreamData.startTimeOfDay) {
                endTimeOfDay += 86400; // Add 24 hours in seconds
            }
            
            const utcEndTime = convertFormDateTimeTimezoneToUnixTimestamp(
                editStreamData.streamDate,
                endTimeOfDay,
                editStreamData.timezone
            );
            
            const updateData = {
                campaignID: editStreamData.campaign._id,
                _id: editStreamData._id,
                startTime: utcStartTime,
                endTime: utcEndTime,
                timeZone: editStreamData.timezone.value,
                status: editStreamData.selectedStreamType,
                withCredentials: true
            };
            
            console.log('Saving stream with status:', editStreamData.selectedStreamType);
            console.log('Full update data:', updateData);
            
            const response = await axios.post(updateStreamRoute, updateData);
            
            toast.success('Stream updated successfully!');
            closeEditModal();
            
            // Refresh campaign data
            fetchCampaignList();
            
        } catch (error) {
            console.error('Error updating stream:', error);
            if (error.response?.data?.error === 'SOCKET_REQUIRED') {
                toast.error('Failed to change stream type');
                setErrorModalData({
                    campaignId: editStreamData.campaign._id,
                    streamId: editStreamData._id,
                    campaignName: editStreamData.campaign.campaignName
                });
                setShowErrorModal(true);
            } else {
                toast.error(error.response?.data?.message || 'Error updating stream');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Helper function to format hour labels based on timeFormat setting
    const formatHourLabel = (hour) => {
        if (timeFormat === '24h') {
            return `${hour.toString().padStart(2, '0')}:00`;
        } else {
            return hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`;
        }
    };

    const renderDayView = () => {
        const hours = Array.from({ length: 24 }, (_, i) => i);
        const dayStreams = getStreamsForDay(currentDate);

        return (
            <div className="calendar-view day-view">
                <div className="calendar-header">
                    <div className="time-header">{selectedTimezone?.abbrev || 'EST'}</div>
                    <div className={`day-header ${isToday(currentDate) ? 'today' : ''}`}>
                        <div className="day-header-content">
                            <div className="day-name">{currentDate.toLocaleDateString('en-US', { weekday: 'long' })}</div>
                            <div className="day-number">{currentDate.getDate()}</div>
                        </div>
                    </div>
                </div>
                <div className="calendar-content">
                    <div className="time-column">
                        {hours.map(hour => (
                            <div key={hour} className="time-slot">
                                {formatHourLabel(hour)}
                            </div>
                        ))}
                    </div>
                    <div className="day-column">
                        <div className="day-content">
                            {hours.map(hour => (
                                <div key={hour} className="hour-slot">
                                    {renderStreamsInHour(dayStreams, hour, currentDate)}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderWeekView = () => {
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
        
        const daysOfWeek = Array.from({ length: 7 }, (_, i) => {
            const day = new Date(startOfWeek);
            day.setDate(startOfWeek.getDate() + i);
            return day;
        });

        const hours = Array.from({ length: 24 }, (_, i) => i);

        return (
            <div className="calendar-view week-view">
                <div className="calendar-header">
                    <div className="time-header">{selectedTimezone?.abbrev || 'EST'}</div>
                    {daysOfWeek.map((day, index) => (
                        <div key={index} className={`day-header ${isToday(day) ? 'today' : ''}`}>
                            <div className="day-header-content">
                                <div className="day-name">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                                <div className="day-number">{day.getDate()}</div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="calendar-content">
                    <div className="time-column">
                        {hours.map(hour => (
                            <div key={hour} className="time-slot">
                                {formatHourLabel(hour)}
                            </div>
                        ))}
                    </div>
                    {daysOfWeek.map((day, index) => (
                        <div key={index} className="day-column">
                            <div className="day-content">
                                {hours.map(hour => (
                                    <div key={hour} className="hour-slot">
                                        {renderStreamsInHour(getStreamsForDay(day), hour, day)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderMonthView = () => {
        const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        
        // Start from the Sunday before the first day of the month
        const startDate = new Date(firstDayOfMonth);
        startDate.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay());
        
        // End on the Saturday after the last day of the month
        const endDate = new Date(lastDayOfMonth);
        endDate.setDate(lastDayOfMonth.getDate() + (6 - lastDayOfMonth.getDay()));

        const weeks = [];
        let currentWeekStart = new Date(startDate);

        while (currentWeekStart <= endDate) {
            const week = Array.from({ length: 7 }, (_, i) => {
                const day = new Date(currentWeekStart);
                day.setDate(currentWeekStart.getDate() + i);
                return day;
            });
            weeks.push(week);
            currentWeekStart.setDate(currentWeekStart.getDate() + 7);
        }

        return (
            <div className="calendar-view month-view">
                <div className="month-header">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="day-name-header">{day}</div>
                    ))}
                </div>
                <div className="month-grid">
                    {weeks.map((week, weekIndex) => (
                        <div key={weekIndex} className="month-week">
                            {week.map((day, dayIndex) => (
                                <div 
                                    key={dayIndex} 
                                    className={`month-day ${
                                        day.getMonth() !== currentDate.getMonth() ? 'other-month' : ''
                                    } ${isToday(day) ? 'today' : ''}`}
                                >
                                    <div className="day-number">{day.getDate()}</div>
                                    <div className="day-streams">
                                        {renderStreamsForMonthDay(getStreamsForDay(day), day.toDateString())}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const getStreamsForDay = (date) => {
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(date);
        dayEnd.setHours(23, 59, 59, 999);

        const streams = [];
        
        if (campaignsData && Array.isArray(campaignsData)) {
            campaignsData.forEach(campaign => {
                if (campaign && campaign.streams && Array.isArray(campaign.streams)) {
                    campaign.streams.forEach(stream => {
                        // Use original times for the raw data
                        const streamStartOriginal = new Date(stream.startTime);
                        const streamEndOriginal = new Date(stream.endTime);
                        
                        // Convert times to selected timezone for display and positioning
                        const streamStart = convertToSelectedTimezone(streamStartOriginal);
                        const streamEnd = convertToSelectedTimezone(streamEndOriginal);
                        
                        // Check if stream overlaps with this day (using converted timezone times)
                        // A stream overlaps if it starts before the day ends AND ends after the day starts
                        if (streamStart < dayEnd && streamEnd > dayStart) {
                            streams.push({
                                ...stream,
                                campaign: campaign,
                                startTime: streamStart,
                                endTime: streamEnd,
                                originalStartTime: streamStartOriginal,
                                originalEndTime: streamEndOriginal
                            });
                        }
                    });
                }
            });
        }

        // Return streams sorted by start time
        return streams.sort((a, b) => a.startTime - b.startTime);
    };

    const renderStreamsInHour = (streams, hour, dayDate) => {
        const dayStart = new Date(dayDate);
        dayStart.setHours(0, 0, 0, 0);
        
        // For hour 0, also include streams that started before this day but extend into it
        // For other hours, only render streams that START in this hour
        const streamsStartingThisHour = streams.filter(stream => {
            if (hour === 0) {
                // At midnight, show:
                // 1. Streams that start at midnight (hour 0) on this day
                // 2. Streams that started before this day (will be clipped at top)
                const streamStartsAtMidnight = stream.startTime.getHours() === 0 && stream.startTime >= dayStart;
                const streamStartedBeforeToday = stream.startTime < dayStart;
                return streamStartsAtMidnight || streamStartedBeforeToday;
            } else {
                // For other hours, only show streams starting in this hour on this day
                return stream.startTime.getHours() === hour && stream.startTime >= dayStart;
            }
        });

        if (streamsStartingThisHour.length === 0) return null;

        // For each stream starting this hour, find all other streams that overlap with it
        // to calculate proper positioning
        return streamsStartingThisHour.map((stream, streamIndex) => {
            // Find ALL streams (from the entire day) that overlap with this stream
            const overlappingStreams = streams.filter(otherStream => {
                return otherStream.startTime < stream.endTime && otherStream.endTime > stream.startTime;
            }).sort((a, b) => a.startTime - b.startTime);

            // Find this stream's position in the overlapping group
            const positionIndex = overlappingStreams.findIndex(s => 
                s.campaign._id === stream.campaign._id && s._id === stream._id
            );
            const totalOverlapping = overlappingStreams.length;

            return renderSingleStream(stream, positionIndex, totalOverlapping, hour, dayDate);
        });
    };

    const renderSingleStream = (stream, index, totalOverlapping, hour, dayDate) => {
        const startTime = formatTime(stream.startTime);
        const endTime = formatTime(stream.endTime);

        // Get the day boundaries (start and end of the current day)
        const dayStart = new Date(dayDate);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayDate);
        dayEnd.setHours(23, 59, 59, 999);

        // Check if stream is clipped
        const isClippedTop = stream.startTime < dayStart;
        const isClippedBottom = stream.endTime > dayEnd;

        // Clip stream times to day boundaries
        const clippedStart = isClippedTop ? dayStart : stream.startTime;
        const clippedEnd = isClippedBottom ? dayEnd : stream.endTime;

        // Calculate duration in hours for height (clipped to day boundaries)
        const durationHours = (clippedEnd - clippedStart) / (1000 * 60 * 60);
        const height = Math.max(durationHours * 60, 30); // 60px per hour, minimum 30px

        // Calculate vertical offset within the hour based on minutes
        // If stream started before this day, offset should be 0
        const startMinutes = isClippedTop ? 0 : stream.startTime.getMinutes();
        const topOffset = (startMinutes / 60) * 60; // Scale to hour height

        // Calculate positioning for overlapping streams
        const widthPercent = totalOverlapping > 1 ? 90 / totalOverlapping : 90;
        const leftPercent = totalOverlapping > 1 ? 
            5 + (index * 90) / totalOverlapping : 5;

        // Build class names for clipping indicators
        const clipClasses = `${isClippedTop ? 'clipped-top' : ''} ${isClippedBottom ? 'clipped-bottom' : ''}`;

        return (
            <div 
                key={`${stream.campaign._id}-${stream._id}-${stream.startTime.getTime()}`} 
                className={`stream-event ${stream.status} ${clipClasses}`}
                style={{
                    height: `${height}px`,
                    top: `${topOffset}px`,
                    width: `${widthPercent}%`,
                    left: `${leftPercent}%`,
                    position: 'absolute',
                    zIndex: 10 + index
                }}
                onClick={() => handleStreamClick(stream)}
            >
                <div className="stream-title">{stream.campaign.campaignName}</div>
                <div className="stream-time">{startTime} - {endTime}</div>
            </div>
        );
    };

    const renderStreamsForMonthDay = (streams, dayKey) => {
        if (streams.length === 0) return null;

        const maxVisible = 3;
        const isExpanded = expandedDays.has(dayKey);
        const visibleStreams = isExpanded ? streams : streams.slice(0, maxVisible);
        const remainingCount = streams.length - maxVisible;

        const toggleExpanded = (e) => {
            e.stopPropagation();
            const newExpandedDays = new Set(expandedDays);
            if (isExpanded) {
                newExpandedDays.delete(dayKey);
            } else {
                newExpandedDays.add(dayKey);
            }
            setExpandedDays(newExpandedDays);
        };

        return (
            <div className={`month-streams ${isExpanded ? 'expanded' : ''}`}>
                {visibleStreams.map((stream, index) => {
                    const startTime = formatTime(stream.startTime);

                    return (
                        <div 
                            key={index} 
                            className={`month-stream-item`}
                            onClick={() => handleStreamClick(stream)}
                        >
                            <span className={`status-bullet ${stream.status || 'scheduled'}`}></span>
                            <span className="stream-time">{startTime}</span>
                            <span className="stream-name">{stream.campaign.campaignName}</span>
                        </div>
                    );
                })}
                {remainingCount > 0 && !isExpanded && (
                    <div className="show-more" onClick={toggleExpanded}>
                        show {remainingCount} more
                    </div>
                )}
            </div>
        );
    };

    const isToday = (date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    return (
        <div className="content-page-container">
            {/* Message bar */}
            <div className="schedule-message-bar">
                iCAST will be down on Wed JAN 8th, 2025 from 1pm to 3pm
            </div>
            
            {/* Banner section */}
            <div className="schedule-banner">
                {/* Banner content will be added later */}
            </div>
            
            <div className="content-page-top content-page-top-schedule">
                <div className="schedule-header">
                    {/* First line: Title and Search */}
                    <div className="schedule-title-row">
                        <h4>Scheduled Livestreams</h4>
                        <div className="spacing-line"></div>
                        <input
                            type="text"
                            placeholder="Search by stream's campaign, or client name"
                            className="schedule-search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                    
                    {/* Second line: Navigation and Controls */}
                    <div className="schedule-nav-row">
                        <div className="date-navigation">
                            <button 
                                className="nav-button today-button"
                                onClick={goToToday}
                            >
                                Today
                            </button>
                            <button 
                                className="nav-button"
                                onClick={() => navigateDate('prev')}
                            >
                                {IconJsxer.GetIcon(IconJsxer.ICONS.chevronLeft, IconJsxer.ICON_STYLES.standard)}
                            </button>
                            <button 
                                className="nav-button"
                                onClick={() => navigateDate('next')}
                            >
                                {IconJsxer.GetIcon(IconJsxer.ICONS.chevronRight, IconJsxer.ICON_STYLES.standard)}
                            </button>
                            <span className="date-range">{getDateRangeText()}</span>
                        </div>
                        
                        <div className="schedule-controls">
                            <div className="view-selector">
                                <DropDownList
                                    id="viewType"
                                    selectFunc={updateFilterHandler}
                                    currentIndex={VIEW_OPTIONS.find(opt => opt.id === viewType)?.index ?? 1}
                                    deselectedLabel="Week"
                                    items={VIEW_OPTIONS}
                                    className="view-dropdown"
                                />
                            </div>
                            
                            <DropDownList
                                id="timeFormat"
                                selectFunc={updateFilterHandler}
                                currentIndex={TIME_FORMAT_OPTIONS.find(opt => opt.id === timeFormat)?.index ?? 0}
                                deselectedLabel="24 hour time"
                                items={TIME_FORMAT_OPTIONS}
                                className="filter"
                            />
                            
                            <DropDownList 
                                id="timezone" 
                                selectFunc={updateFilterHandler}
                                currentIndex={(selectedTimezone && timezoneOptions.length > 0) ? (selectedTimezone.index ?? 0) : 0}
                                deselectedLabel="Local timezone"
                                items={timezoneOptions.length > 0 ? timezoneOptions : [
                                    { id: 'gmt', label: 'Loading timezones...', value: 'GMT', index: 0, offset: 0 }
                                ]}
                                className="filter timezone-dropdown"
                            />
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="content-page-content-container">
                <div className="content-page-main-content">
                    <div className="schedule-container">
                        {viewType === 'day' && renderDayView()}
                        {viewType === 'week' && renderWeekView()}
                        {viewType === 'month' && renderMonthView()}
                    </div>
                </div>
            </div>
            
            {/* Stream Details Modal */}
            {isModalOpen && selectedStream && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                {selectedStream.title || selectedStream.campaign.campaignName}
                            </h3>
                            <div className="modal-actions">
                                <button className="edit-icon-btn" onClick={handleEditStream}>
                                    {IconJsxer.GetIcon(IconJsxer.ICONS.edit, IconJsxer.ICON_STYLES.settingsEdit)}
                                </button>
                                <button className="modal-close" onClick={closeModal}>
                                    {IconJsxer.GetIcon(IconJsxer.ICONS.closeX, IconJsxer.ICON_STYLES.standard)}
                                </button>
                            </div>
                        </div>
                        
                        <div className="modal-body">
                            <div className="modal-field">
                                <label>Day</label>
                                <div>{selectedStream.startTime.toLocaleDateString('en-US', { 
                                    weekday: 'long',
                                    day: 'numeric', 
                                    month: 'long', 
                                    year: 'numeric' 
                                })}</div>
                            </div>
                            
                            <div className="modal-field">
                                <label>Time</label>
                                <div>
                                    {formatTime(selectedStream.startTime)} - {formatTime(selectedStream.endTime)}
                                    {selectedTimezone && (
                                        <small>({selectedTimezone.label})</small>
                                    )}
                                </div>
                            </div>
                            
                            <div className="modal-field">
                                <label>Stream type</label>
                                <div>{STREAM_STATUS_TYPES[selectedStream.status]?.displayName || 'Live stream'}</div>
                            </div>
                            
                            <div className="modal-field">
                                <label>OBS link</label>
                                {selectedStream.campaign && selectedStream._id ? (
                                    <div className="obs-link-container">
                                        <input 
                                            type="text" 
                                            value={`${config.axiosOrigin}/obs-feature/${selectedStream.campaign._id}/${selectedStream._id}/321ABC`} 
                                            readOnly 
                                            className="obs-link-input"
                                        />
                                        <button 
                                            className="copy-link-btn"
                                            onClick={() => {
                                                const obsLink = `${config.axiosOrigin}/obs-feature/${selectedStream.campaign._id}/${selectedStream._id}/321ABC`;
                                                navigator.clipboard.writeText(obsLink);
                                                toast.success('OBS link copied to clipboard');
                                            }}
                                        >
                                            {IconJsxer.GetIcon(IconJsxer.ICONS.copy, IconJsxer.ICON_STYLES.standard)}
                                            Copy link
                                        </button>
                                    </div>
                                ) : (
                                    <div className="missing-data-info">No OBS link</div>
                                )}
                            </div>
                            
                            <div className="modal-field">
                                <label>Streamers</label>
                                {selectedStream.channels && selectedStream.channels.length > 0 ? (
                                    <div className="streamers-list">
                                        {selectedStream.channels.map((streamer, index) => (
                                            <div key={index} className="streamer-item">
                                                {streamer.profilePic ? (
                                                    <img 
                                                        src={streamer.profilePic} 
                                                        alt={streamer.twitchHandle}
                                                        className="streamer-avatar"
                                                        onError={(e) => {
                                                            console.error('Failed to load image:', streamer.profilePic);
                                                            e.target.style.display = 'none';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="streamer-avatar"></div>
                                                )}
                                                <span>{streamer.twitchHandle}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="missing-data-info">No streamers</div>
                                )}
                            </div>
                            
                            <div className="modal-field">
                                <label>Client</label>
                                <div>{selectedStream.campaign.client || selectedStream.campaign.campaignOwner || 'Fan duel'}</div>
                            </div>
                            
                            <div className="modal-field">
                                <label>Campaign</label>
                                <div className="campaign-info">
                                    <span>{selectedStream.campaign.campaignName}</span>
                                    <button 
                                        className="open-campaign-link"
                                        onClick={() => handleOpenCampaign(selectedStream.campaign._id)}
                                    >
                                        Open campaign
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Edit Stream Modal */}
            {isEditModalOpen && editStreamData && (
                <div className="modal-overlay" onClick={closeEditModal}>
                    <div className="modal-content edit-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header edit-header">
                            <div className="header-content">
                                <div className="campaign-header">
                                    <button className="back-btn" onClick={goBackToViewModal} type="button">
                                        {IconJsxer.GetIcon(IconJsxer.ICONS.chevronLeft, IconJsxer.ICON_STYLES.standard)}
                                    </button>
                                    <span className="campaign-name">{editStreamData.campaign.campaignName}</span>
                                </div>
                                <div className="stream-header">
                                    <h3>Edit stream</h3>
                                    <button className="delete-link" onClick={handleDeleteStream} type="button">
                                        Delete stream
                                    </button>
                                </div>
                            </div>
                            <button className="modal-close" onClick={closeEditModal}>
                                {IconJsxer.GetIcon(IconJsxer.ICONS.closeX, IconJsxer.ICON_STYLES.standard)}
                            </button>
                        </div>
                        
                        <form onSubmit={handleSaveEditedStream} className="edit-form">
                            <div className="modal-field">
                                <label>Day</label>
                                <DateSelect 
                                    id="edit_stream_date"
                                    allowPast={true}
                                    initialDate={editStreamData.streamDate}
                                    selectFunc={(id, val) => handleEditFieldChange('streamDate', val)}
                                    className="date-picker"
                                />
                            </div>
                            
                            <div className="modal-field">
                                <label>Time</label>
                                <div className="time-range">
                                    <div className="time-input-group">
                                        <DropDownList
                                            id="start_time"
                                            selectFunc={(e, id, item) => handleEditFieldChange('startTimeOfDay', item.value)}
                                            currentIndex={timeOptions.findIndex(opt => opt.value === editStreamData.startTimeOfDay) || 0}
                                            deselectedLabel="Select time"
                                            items={timeOptions}
                                            className="time-dropdown"
                                        />
                                    </div>
                                    <span className="time-separator">to</span>
                                    <div className="time-input-group">
                                        <DropDownList
                                            id="end_time"
                                            selectFunc={(e, id, item) => handleEditFieldChange('endTimeOfDay', item.value)}
                                            currentIndex={timeOptions.findIndex(opt => opt.value === editStreamData.endTimeOfDay) || 0}
                                            deselectedLabel="Select time"
                                            items={timeOptions}
                                            className="time-dropdown"
                                        />
                                    </div>
                                    <div className="timezone-info">
                                        ({editStreamData.timezone.label})
                                    </div>
                                </div>
                            </div>
                            
                            <div className="modal-field">
                                <label>Stream type</label>
                                <DropDownList
                                    id="stream_type"
                                    selectFunc={(e, id, item) => handleEditFieldChange('selectedStreamType', item.value)}
                                    currentIndex={streamTypeOptions.findIndex(opt => opt.value === editStreamData.selectedStreamType) || 0}
                                    deselectedLabel="Select stream type"
                                    items={streamTypeOptions}
                                    className="stream-type-dropdown"
                                />
                            </div>
                            
                            <div className="modal-field">
                                <label>Streamers</label>
                                <div className="streamers-edit">
                                    {(() => {
                                        console.log('Rendering streamers, editStreamData.channels:', editStreamData.channels);
                                        if (editStreamData.channels) {
                                            editStreamData.channels.forEach((ch, i) => {
                                                console.log(`Channel ${i}:`, ch.twitchHandle, 'profilePic:', ch.profilePic);
                                            });
                                        }
                                        return null;
                                    })()}
                                    {editStreamData.channels && editStreamData.channels.length > 0 ? (
                                        <div className="streamers-list">
                                            {editStreamData.channels.map((streamer, index) => (
                                                <div key={index} className="streamer-item">
                                                    {streamer.profilePic ? (
                                                        <img 
                                                            src={streamer.profilePic} 
                                                            alt={streamer.twitchHandle}
                                                            className="streamer-avatar"
                                                            onError={(e) => {
                                                                console.error('Failed to load image:', streamer.profilePic);
                                                                e.target.style.display = 'none';
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="streamer-avatar"></div>
                                                    )}
                                                    <span>{streamer.twitchHandle}</span>
                                                    <button 
                                                        className="remove-streamer-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveStreamer(index);
                                                        }}
                                                        type="button"
                                                    >
                                                        {IconJsxer.GetIcon(IconJsxer.ICONS.closeX, IconJsxer.ICON_STYLES.standard)}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="missing-data-info">No streamers</div>
                                    )}
                                    <button type="button" className="add-streamer-btn" onClick={handleAddStreamer}>
                                        + Add streamer
                                    </button>
                                </div>
                            </div>
                            
                            <div className="form-actions">
                                {/* {<button 
                                    type="button" 
                                    className="cancel-btn"
                                    onClick={closeEditModal}
                                >
                                    Cancel
                                </button>} */}
                                <button 
                                    type="submit" 
                                    className={`submit-btn${isSubmitting ? ' submitting' : ''}${!hasEditChanges ? ' disabled' : ''}`}
                                    disabled={isSubmitting || !hasEditChanges}
                                >
                                    {isSubmitting ? 'Confirm changes...' : 'Confirm changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
            {/* Add Streamer Modal */}
            {showAddStreamer && editStreamData && (
                <AddStreamChannel
                    campaignId={editStreamData.campaign._id}
                    streamID={editStreamData._id}
                    closePanelFunc={handleCloseAddStreamer}
                />
            )}
            
            {/* Error Modal for Socket Requirement */}
            {showErrorModal && errorModalData && (
                <div className="modal-overlay error-modal-overlay" onClick={() => setShowErrorModal(false)}>
                    <div className="modal-content error-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Stream Not Set Up</h3>
                            <button className="modal-close" onClick={() => setShowErrorModal(false)}>
                                {IconJsxer.GetIcon(IconJsxer.ICONS.closeX, IconJsxer.ICON_STYLES.standard)}
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            <p className="error-message">
                                This stream hasn't been activated yet and cannot be set to live from the schedule view.
                            </p>
                            <p className="error-explanation">
                                To activate a stream and set it to live, you need to navigate to the campaign's stream management page where you can configure all necessary settings including:
                            </p>
                            <ul className="error-requirements">
                                <li>Twitch channel connections</li>
                                <li>Chat bots and commands</li>
                                <li>Feature configurations</li>
                                <li>Stream activation</li>
                            </ul>
                            <p className="error-action">
                                Would you like to go to the stream setup page now?
                            </p>
                        </div>
                        
                        <div className="modal-footer">
                            <button 
                                className="secondary-button"
                                onClick={() => setShowErrorModal(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                className="primary-button"
                                onClick={() => {
                                    navigate(TOP_LEVEL_NAV_ROUTES.MANAGE_CAMPAIGN_STREAM
                                        .replace(':campaignId', errorModalData.campaignId)
                                        .replace(':streamId', errorModalData.streamId));
                                    setShowErrorModal(false);
                                }}
                            >
                                Go to Stream Setup
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Schedule;
